/**
 * Queue Journeys — the customer-experience designer library (Queues-section
 * tab, route `queues.flows`) + the journey designer page (route `queues.flow`).
 * List paradigm matches the Events section: List/Cards toggle, search + type
 * filter, click-to-sort columns, Open · Edit · Clone · ⋯ on every item.
 * Journeys persist via the declared queue-flow ops (awaited).
 */
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Btn, Icon, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { FilterBar, FilterDropdown, EmptyState, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CardActionBar, CARD_GRID } from "../../shell/list-composites";
import { useIsMobile } from "../../shell/useResponsive";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { useWfResource, useBusy } from "../workforce/useWorkforce";
import { WfPending } from "../workforce/wf-shared";
import { listQueueFlows, saveQueueFlow, deleteQueueFlow, listQueueTypes, type QueueFlowRecord, type QueueType } from "./queues-api";
import { qfDefaultFlow, QF_TRIGGER, type QFFlow, type QFStep } from "./qf-model";
import { QueueFlowBuilder } from "./QueueFlowBuilder";
import { queuesSectionTabs, useQueueSectionCounts, SORT_ARROW } from "./queues-section";

import { useT } from "@v0y4ge/i18n-runtime";
function countSteps(steps: QFStep[]): { steps: number; messages: number } {
  let n = 0, m = 0;
  const walk = (arr: QFStep[]) => arr.forEach((s) => { n++; if (s.kind === "comms" || s.kind === "survey") m++; if (s.kind === "decision") { walk(s.yes); walk(s.no); } });
  walk(steps);
  return { steps: n, messages: m };
}

const thStyle: CSSProperties = { padding: "10px 16px", textAlign: "left", fontWeight: 600, fontSize: 12, color: T.soft, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" };
const tdStyle: CSSProperties = { padding: "12px 16px", verticalAlign: "middle", fontSize: 13, color: T.n2, borderBottom: `1px solid ${T.border}` };

export function QueueFlowsLibrary() {
  const t = useT();
  const nav = useNav();
  const isMobile = useIsMobile();
  const counts = useQueueSectionCounts();
  const writable = can("queueflows.write");
  const { data, loading, error, reload } = useWfResource<{ flows: QueueFlowRecord[] }>("qflows", () => listQueueFlows());
  const { data: typesData } = useWfResource<{ types: QueueType[] }>("qtypes", () => listQueueTypes());
  const { busy, run } = useBusy();
  const [view, setView] = useViewMode("qjourneys", "grid");
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [sort, setSort] = useState("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const typeLabel = (id: string | null) => typesData?.types.find((t) => t.id === id)?.label ?? "Any queue type";

  const onHeaderSort = (k: string) => {
    if (sort === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(k); setSortDir(["steps", "usedBy"].includes(k) ? "desc" : "asc"); }
  };
  const rows = useMemo(() => {
    const qy = query.trim().toLowerCase();
    let out = (data?.flows ?? []).filter((f) =>
      (!qy || f.name.toLowerCase().includes(qy) || f.desc.toLowerCase().includes(qy)) &&
      (!typeFilter || typeLabel(f.typeId) === typeFilter));
    const val = (f: QueueFlowRecord): string | number => sort === "type" ? typeLabel(f.typeId) : sort === "steps" ? countSteps(f.steps as QFStep[]).steps
      : sort === "usedBy" ? f.usedBy : sort === "updated" ? f.updatedAt : f.name.toLowerCase();
    out = [...out].sort((a, b) => { const x = val(a), y = val(b); const base = x < y ? -1 : x > y ? 1 : 0; return sortDir === "desc" ? -base : base; });
    return out;
  }, [data, query, typeFilter, sort, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = (f: QueueFlowRecord) => run(`del:${f.id}`, async () => {
    await deleteQueueFlow(f.id);
    toast({ tone: "success", title: "Journey deleted", subtitle: `"${f.name}" removed from the library.` });
    reload();
  });
  const clone = (f: QueueFlowRecord) => run(`cl:${f.id}`, async () => {
    await saveQueueFlow({ id: `qf_${Date.now().toString(36)}`, name: `${f.name} (copy)`, typeId: f.typeId, desc: f.desc, trigger: f.trigger, steps: JSON.parse(JSON.stringify(f.steps)) });
    toast({ tone: "success", title: "Journey cloned", subtitle: `"${f.name} (copy)" created.` });
    reload();
  });

  const actions = (f: QueueFlowRecord) => ({
    onView: () => nav.go("queues.flow", { id: f.id }),
    viewLabel: "Open journey",
    onEdit: writable ? () => nav.go("queues.flow", { id: f.id }) : undefined,
    editLabel: "Edit journey",
    onDuplicate: writable ? () => clone(f) : undefined,
    duplicateLabel: "Clone journey",
    moreHeader: f.name,
    moreItems: writable ? [{ icon: <Icon.trash size={14} />, label: "Delete journey", danger: true, onClick: () => remove(f) }] : [],
  });

  const filtersActive = !!(query || typeFilter);
  const listView = (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
        <thead>
          <tr>
            {[["name", "Journey"], ["type", "Queue type"], ["steps", "Steps"], ["usedBy", "Used by"], ["updated", "Updated"]].map(([k, label]) => (
              <th key={k} style={thStyle} onClick={() => onHeaderSort(k)} aria-sort={sort === k ? (sortDir === "asc" ? "ascending" : "descending") : undefined}>{label}{SORT_ARROW(sort === k, sortDir)}</th>
            ))}
            <th style={{ ...thStyle, cursor: "default", textAlign: "right" }}>{t('queues.flowsPages.text')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => {
            const c = countSteps(f.steps as QFStep[]);
            return (
              <tr key={f.id} data-testid="qjourneys-table-row" data-id={f.id} style={{ cursor: "pointer" }} onClick={() => nav.go("queues.flow", { id: f.id })}>
                <td style={{ ...tdStyle, fontWeight: 700, color: T.n1 }}>
                  {f.name}
                  <div style={{ fontSize: 11.5, fontWeight: 400, color: T.soft, marginTop: 2 }}>{t('queues.flowsPages.text2')}{" "}{QF_TRIGGER(f.trigger?.event ?? "joined").label.toLowerCase()}</div>
                </td>
                <td style={tdStyle}><Pill tone={f.typeId === "quick" ? "info" : f.typeId === "standard" ? "primary" : "neutral"}>{typeLabel(f.typeId)}</Pill></td>
                <td style={{ ...tdStyle, fontFamily: T.fontMono }}>{c.steps} <span style={{ color: T.soft, fontFamily: T.font }}>· {c.messages} {t('queues.flowsPages.text3')}</span></td>
                <td style={{ ...tdStyle, fontFamily: T.fontMono }}>{f.usedBy} {t('queues.flowsPages.text4')}{" "}{f.usedBy === 1 ? "" : t('queues.flowsPages.text5')}</td>
                <td style={tdStyle}>{new Date(f.updatedAt).toLocaleDateString([], { month: "short", day: "numeric" })}</td>
                <td style={tdStyle} onClick={(e) => e.stopPropagation()}><RowActions {...actions(f)} testid="qjourneys-row" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const cardView = (
    <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 14 }}>
      {rows.map((f) => {
        const c = countSteps(f.steps as QFStep[]);
        return (
          <div key={f.id} data-testid="qjourneys-card" data-id={f.id}
            style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: T.shadowSm, overflow: "hidden", display: "flex", flexDirection: "column", cursor: "pointer" }}
            onClick={() => nav.go("queues.flow", { id: f.id })}>
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={{ width: 34, height: 34, borderRadius: 9, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.network size={16}/></span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</div>
                  <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{typeLabel(f.typeId)} {t('queues.flowsPages.text6')}{" "}{QF_TRIGGER(f.trigger?.event ?? "joined").label.toLowerCase()}</div>
                </div>
              </div>
              {f.desc ? <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>{f.desc}</div> : null}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: "auto" }}>
                <Pill tone="neutral">{c.steps} {t('queues.flowsPages.text7')}</Pill>
                <Pill tone="info">{c.messages} {t('queues.flowsPages.text8')}</Pill>
              </div>
            </div>
            <CardActionBar left={<span>{f.usedBy} {t('queues.flowsPages.text4')}{" "}{f.usedBy === 1 ? "" : t('queues.flowsPages.text5')}</span>} {...actions(f)} testid="qjourneys-card" />
          </div>
        );
      })}
    </div>
  );

  return (
    <Screen rail="q-board" tabs={queuesSectionTabs(nav, "journeys", counts)} activeTab="journeys">
      <PageHeader title={t('queues.flowsPages.title5')} subtitle={t('queues.flowsPages.subtitle')} breadcrumb={null}
        actions={<>
          <ViewToggle mode={view} onChange={setView} testid="qjourneys-view" />
          {writable ? <Btn icon={<Icon.plus size={14}/>} onClick={() => nav.go("queues.flow")} testid="qjourneys-new-btn">{t('queues.flowsPages.text9')}</Btn> : null}
        </>} />
      <DataSourceStrip screen="qjourneys" ops={["listQueueFlows", "listQueueTypes", "saveQueueFlow", "deleteQueueFlow"]} />
      <FilterBar>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('queues.flowsPages.placeholder')} aria-label={t('queues.flowsPages.ariaLabel')} data-testid="qjourneys-search-input"
          style={{ height: 34, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontSize: 13, fontFamily: T.font, width: 220 }} />
        <FilterDropdown label={typeFilter || "Any queue type"} active={!!typeFilter} options={(typesData?.types ?? []).map((t) => t.label)} onPick={setTypeFilter} onClear={() => setTypeFilter(null)} />
        {filtersActive ? <button onClick={() => { setQuery(""); setTypeFilter(null); }} style={{ padding: "6px 10px", borderRadius: 999, fontSize: 12, fontWeight: 500, background: "transparent", color: T.p1, border: "none", cursor: "pointer", fontFamily: T.font }}>{t('queues.flowsPages.text10')}</button> : null}
      </FilterBar>
      <div style={{ padding: "0 24px 24px", flex: 1, overflow: "auto" }}>
        {error ? <WfPending screen="qjourneys" opName="listQueueFlows" error={error} onRetry={reload} />
        : loading ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t('queues.flowsPages.text11')}</div>
        : rows.length === 0 ? (
          filtersActive
            ? <EmptyState icon={<Icon.network size={20}/>} title="No journeys match" body="Try clearing the search or type filter." action={<Btn variant="secondary" onClick={() => { setQuery(""); setTypeFilter(null); }}>Clear filters</Btn>} />
            : <EmptyState icon={<Icon.network size={20}/>} title="No queue journeys yet" body="A journey defines the customer experience: join confirmation, waiting, your-turn messages, service tracking and CSAT follow-up." action={writable ? <Btn onClick={() => nav.go("queues.flow")}>Design your first journey</Btn> : undefined} />
        ) : view === "list" && !isMobile ? listView : cardView}
      </div>
    </Screen>
  );
}

export function QueueFlowBuilderPage() {
  const t = useT();
  const nav = useNav();
  const readOnly = !can("queueflows.write");
  const flowId = (nav.params?.id ?? null) as string | null;
  const { data, loading } = useWfResource<{ flows: QueueFlowRecord[] }>("qflows", () => listQueueFlows());
  const [flow, setFlow] = useState<QFFlow>(() => qfDefaultFlow());
  const [meta, setMeta] = useState({ id: "", name: "New queue journey", typeId: null as string | null, desc: "" });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!flowId || loading) return;
    const f = (data?.flows ?? []).find((x) => x.id === flowId);
    if (!f) return;
    setFlow({ trigger: f.trigger, steps: JSON.parse(JSON.stringify(f.steps)) });
    setMeta({ id: f.id, name: f.name || "Untitled journey", typeId: f.typeId, desc: f.desc });
    setDirty(false);
  }, [flowId, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveQueueFlow({ id: meta.id || `qf_${Date.now().toString(36)}`, name: meta.name || "Untitled journey", typeId: meta.typeId, desc: meta.desc, trigger: flow.trigger, steps: flow.steps });
      setMeta((m) => ({ ...m, id: saved.id }));
      setDirty(false);
      toast({ tone: "success", title: "Journey saved", subtitle: `"${meta.name}" saved to the library.` });
    } catch (e) {
      toast({ tone: "danger", title: "Couldn't save the journey", subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setSaving(false); }
  };

  const wrap: CSSProperties = { flex: 1, display: "flex", minHeight: 0, margin: "0 24px 24px" };
  return (
    <Screen rail="q-board">
      <PageHeader
        title={readOnly ? (meta.name || "Untitled journey")
          : <input value={meta.name} placeholder={t('queues.flowsPages.placeholder2')} aria-label={t('queues.flowsPages.ariaLabel2')}
              size={Math.max(24, (meta.name || "").length + 2)}
              onChange={(e) => { setMeta((m) => ({ ...m, name: e.target.value })); setDirty(true); }}
              style={{ font: "inherit", color: "inherit", border: "none", borderBottom: `1px dashed ${T.n4}`, background: "transparent", outline: "none", padding: "0 2px 2px", maxWidth: "100%", fontFamily: T.font }} />}
        subtitle={readOnly ? undefined : "Click the title to rename · click a page on the canvas to design it"}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("queues.flows")}><Icon.arrow dir="left" size={11} /> {t('queues.flowsPages.text13')}</span>}
        actions={<>
          {dirty && !readOnly ? <span style={{ fontSize: 12, color: T.soft, alignSelf: "center" }}>{t('queues.flowsPages.text14')}</span> : null}
          {!readOnly ? <Btn onClick={save} testid="qflow-save-btn" style={{ opacity: saving ? .6 : 1 }}>{saving ? t('queues.flowsPages.text15') : t('queues.flowsPages.text16')}</Btn> : null}
        </>}
      />
      <DataSourceStrip screen="qflow" ops={["listQueueFlows", "saveQueueFlow"]} />
      <div style={wrap}>
        <QueueFlowBuilder flow={flow} onFlow={(next) => { setFlow(next); setDirty(true); }} readOnly={readOnly} height="100%" />
      </div>
    </Screen>
  );
}
export default QueueFlowBuilderPage;
